import { useState } from "react";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Loader2, Copy, KeyRound } from "lucide-react";

const err = (e, fallback) => (typeof e.response?.data?.detail === "string" ? e.response.data.detail : fallback);

export function BackupCodes({ codes, onDone }) {
  const copy = () => { navigator.clipboard?.writeText(codes.join("\n")).then(() => toast.success("Backup codes copied")).catch(() => {}); };
  return (
    <div className="space-y-3" data-testid="mfa-backup-codes">
      <p className="text-sm text-muted-foreground">Save these 10 backup codes somewhere safe. Each one works once if you lose your phone. You won't see them again.</p>
      <div className="grid grid-cols-2 gap-2 font-mono text-sm">
        {codes.map((c, i) => <motion.span key={c} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }} className="rounded-md border border-border bg-muted/40 px-2.5 py-1.5 text-center" data-testid={`backup-code-${i}`}>{c}</motion.span>)}
      </div>
      <div className="flex gap-2">
        <Button variant="outline" onClick={copy} className="gap-1.5" data-testid="mfa-copy-codes"><Copy className="h-4 w-4" /> Copy codes</Button>
        {onDone && <Button onClick={onDone} className="flex-1" data-testid="mfa-codes-done">I've saved them</Button>}
      </div>
    </div>
  );
}

// QR + code entry. mfaToken is set when setup happens during sign-in (org requires 2-step).
export function MfaSetup({ mfaToken, onEnabled }) {
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const start = async () => {
    setBusy(true);
    try { const { data } = await api.post("/auth/2fa/setup", { mfa_token: mfaToken }); setSetup(data); }
    catch (e) { toast.error(err(e, "Could not start setup")); }
    finally { setBusy(false); }
  };
  const enable = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { const { data } = await api.post("/auth/2fa/enable", { code, mfa_token: mfaToken }); onEnabled(data); }
    catch (e2) { toast.error(err(e2, "That code didn't match")); }
    finally { setBusy(false); }
  };
  if (!setup) return <Button onClick={start} disabled={busy} className="gap-1.5" data-testid="mfa-start-setup">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />} Set up authenticator app</Button>;
  return (
    <form onSubmit={enable} className="space-y-3" data-testid="mfa-setup">
      <p className="text-sm text-muted-foreground">Scan this with Google Authenticator, Microsoft Authenticator or a similar app, then type the 6-digit code it shows.</p>
      <div className="flex flex-wrap items-center gap-4">
        <img src={setup.qr} alt="Authenticator QR code" className="h-40 w-40 rounded-lg bg-white p-1.5" data-testid="mfa-qr" />
        <div className="text-xs text-muted-foreground space-y-1 min-w-0">
          <div>Can't scan? Enter this key:</div>
          <code className="block break-all rounded bg-muted/50 px-2 py-1 text-foreground" data-testid="mfa-secret">{setup.secret}</code>
        </div>
      </div>
      <div className="flex gap-2">
        <Input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" autoComplete="one-time-code" placeholder="123456" className="h-11 font-mono tracking-[0.3em] max-w-[180px]" data-testid="mfa-setup-code" />
        <Button type="submit" disabled={busy || code.length !== 6} data-testid="mfa-enable">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Turn on"}</Button>
      </div>
    </form>
  );
}
